import React, {useMemo, useState} from "react";
import { connect } from 'react-redux';
import {Fab, IconButton, Paper, Tooltip, Typography, withStyles} from '@material-ui/core';
import MaterialTable from "material-table";
import Toolbar from "../../sc-utilities/Toolbar";
import {BackButton, hasPermission, localization, hasAtLeastOnePermission} from "../../sc-utilities";
import * as securazeSolutionActions from './store/securazeSolutionActions';
import {Add, Delete, Refresh} from "@material-ui/icons";
import * as action from '../../np-core/Auth/store/authActions';
import ProductTypeDelete from "../ProductType/ProductTypeDelete";
import * as actions from '../ErasureMethod/store/erasureMethodActions';

const styles = theme => ({
    "@global": {
        "[class*='MuiTableCell-root MuiTableCell-body']": {
            height: "40px",
            padding: '10px',
            fontSize: '1rem'
        },
    },
    fab: {
        textTransform: 'none',
        color: theme.palette.buttonText.default,
        marginLeft: theme.spacing(),
        backgroundColor: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        }
    },
    customToolbar: {
        marginLeft: 14,
        width: "900px"
    },
    tableTitle: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        overflow: 'visible',
    },
    customTooltip: {
        marginLeft: -16,
        marginTop: '-2px'
    }
});

let pageSize = 100;
const rowsPageSizeInit = [20, 50, 100];

const SecurazeSolutionList = (props) => {
    const { classes, tableRef, translations, tableColumns, activeCustomerID, saveColumnsFilter, fetching, permissions, onFetchSecurazeSolutionsCount, onFetchSecurazeSolutions, onBulkDeleteSecurazeSolutions, redirectHandler } = props;

    const [deleteModalState, setDeleteModalState] = useState({ open: false, ids: [] });
    const selectionPermissions = hasAtLeastOnePermission(permissions, ['securaze_solution_delete']);

    const columnObject = useMemo(() => tableColumns
        // eslint-disable-next-line react-hooks/exhaustive-deps
        , []);

    const [tableOptions, setTableOptions] = useState({
        filtering: true,
        sorting: false,
        search: false,
        selection: selectionPermissions,
        minBodyHeight: window.innerHeight - 197 < 300 ? 300 : window.innerHeight - 197,
        maxBodyHeight: window.innerHeight - 197,
        pageSizeOptions: rowsPageSizeInit,
        exportButton: false,
        rowStyle: (_rowData, index) => {
            return { backgroundColor: index % 2 ? '#f3f3f3' : '#ffffff' };
        },
        columnsButton: true,
        pageSize: pageSize ? pageSize : 50,
        emptyRowsWhenPaging: false
    });

    const filterQuery = filters => {
        let obj = {};
        for (var filter of filters) {
            obj[filter.column.field] = filter.value;
        }
        return obj;
    }

    async function fetchTableData(query) {
        const queryCopy = {...query};
        const q = filterQuery(queryCopy.filters);
        queryCopy['ActiveCustomerID'] = activeCustomerID;
        if(q.SupportedClientTypes.length === 0){
            delete q["SupportedClientTypes"];
        }
        queryCopy['q'] = q;
        delete queryCopy["search"];
        delete queryCopy["orderDirection"];
        delete queryCopy["totalCount"];
        delete queryCopy["filters"];
        const count = await onFetchSecurazeSolutionsCount(queryCopy);
        const result = count ? await onFetchSecurazeSolutions(queryCopy) : [];
        setTableOptions({ ...tableOptions, pageSize: queryCopy['pageSize'], pageSizeOptions: rowsPageSizeInit });
        return { data: result, page: queryCopy.page, pageSize: pageSize, totalCount: count };
    }

    const columnHidden = newColumnState => {
        let objToSendColumn = {}
        let objColumns = {}
        columnObject.map(column => {
            objColumns[`${column.field}`] = column.hidden
            return objColumns
        })
        objToSendColumn[`SecurazeSolutions`] = objColumns
        saveColumnsFilter({ ...objToSendColumn, ActiveCustomerID: activeCustomerID })
        return newColumnState
    }

    const tableTitle = () => {
        return <div className={classes.customToolbar}>
            <div className={classes.tableTitle}>
                {BackButton(translations, classes.customTooltip)}
                <Typography >{translations['securaze_solution'] || 'Securaze Solution'}</Typography>
                {hasPermission(permissions, 'securaze_solution_create') &&
                <Fab
                    id="create_securaze_solution"
                    variant="extended"
                    size="small"
                    color="primary"
                    aria-label="Add"
                    style={{ borderRadius: '1px' }}
                    className={classes.fab}
                    onClick={() => redirectHandler(`/admin/securaze-solution/create`)}
                >
                    <Add size="small" />
                    {translations["create_new"] || "Create new"}
                </Fab>}
                <Tooltip title={translations["refresh"] || "Refresh"}>
                    <IconButton aria-label={translations["refresh"] || "Refresh"} component="div" disabled={fetching} onClick={() => tableRef.current && tableRef.current.onQueryChange()}>
                        <Refresh />
                    </IconButton>
                </Tooltip>
            </div>
        </div>
    }

    const handleCloseSafe = () => {
        setDeleteModalState((previousState) => ({ ...previousState, open: false, ids: []}));
    }

    const handleCloseDanger = async () => {
        await onBulkDeleteSecurazeSolutions({ SecurazeSolutionIDs: deleteModalState.ids, ActiveCustomerID: activeCustomerID });
        setDeleteModalState((previousState) => ({ ...previousState, open: false, ids: []}));
        tableRef.current && tableRef.current.onQueryChange()
    };

    const handleBulkDelete = ids => {
        setDeleteModalState({ open: true, ids: ids, forBulk: true });
    };

    const tableActions = [
        {
            tooltip: `${translations["remove_all_selected"] || "Remove all selected"} ${translations["securaze_solutions"] || "Securaze Solutions"}`,
            icon: <Delete />,
            onClick: (rowData) => handleBulkDelete(rowData.map(securazeSolution => securazeSolution.SecurazeSolutionID))
        }
    ];

    return (
        <Paper>
            <MaterialTable
                components={{
                    Toolbar: props => (
                        <Toolbar data={props} actions={tableActions} selectedRows={props.selectedRows} translations={translations} />
                    )
                }}
                localization={localization(translations)}
                tableRef={tableRef}
                title={tableTitle()}
                columns={columnObject}
                data={fetchTableData}
                options={tableOptions}
                onChangeRowsPerPage={(size) => {
                    pageSize = size
                }}
                onChangeColumnHidden={columnHidden}
                onChangePage={() => {if(tableRef?.current?.tableContainerDiv?.current) {tableRef.current.tableContainerDiv.current.scrollTop = 0}}}
            />
            {hasPermission(permissions, 'securaze_solution_delete') ? (
                <ProductTypeDelete
                    activeCustomer={activeCustomerID}
                    entityName={translations['securaze_solutions'] || 'Securaze Solutions'}
                    translations={translations}
                    handleCloseDanger={handleCloseDanger}
                    handleCloseSafe={handleCloseSafe}
                    deleteModalState={deleteModalState}
                />
            ) : null}
        </Paper>
    )
}

const mapStateToProps = (state) => {
    return {
        activeCustomerID: state.core.login.activeCustomer.CustomerID,
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        permissions: state?.core?.login?.permissions || [],
    }
}

const mapDispatchToProps = (dispatch) => {
    return {
        onFetchSecurazeSolutionsCount: (params) => dispatch(securazeSolutionActions.fetchSecurazeSolutionsCount(params)),
        onFetchSecurazeSolutions: (params) => dispatch(securazeSolutionActions.fetchSecurazeSolutions(params)),
        onBulkDeleteSecurazeSolutions: (params) => dispatch(securazeSolutionActions.bulkDeleteSecurazeSolutions(params)),
        onListSupportedClientTypes: (data, option) => dispatch(actions.listSupportedClientTypes(data, option)),
        saveColumnsFilter: data => dispatch(action.saveColumnsFilter(data)),
    }
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles)(SecurazeSolutionList));